import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Descriptions as AntdDescriptions, Space, Typography } from 'antd';
import styled from 'styled-components';
import { CLAIM_STATUSES } from 'all-in-one-shared-api';

import { StyledTitle } from 'components/StyledTitle';
import { DocumentLink } from 'components/documentLink/DocumentLink';
import { HomeContext } from 'contexts/home/context';
import { type FormattedClaimDetails } from 'utils/formatClaimDetails';
import { getUploadDocPageUrl } from 'utils';
// import { color } from 'html2canvas/dist/types/css/types/color';

const { Text } = Typography;

const TitleSection = styled(Space)`
  margin-bottom: 1rem;
`;

const BravoPlusIcon = styled(PlusOutlined)`
  color: var(--primary-color);
`;

const Descriptions = styled(AntdDescriptions)`
  background-color: var(--white);
`;

export type SupplementaryDocumentsProps = Pick<
  FormattedClaimDetails,
  | 'claimId'
  | 'sequenceNo'
  | 'claimStatus'
  | 'receiptFiles'
  | 'refLetters'
  | 'otherDocs'
  | 'receiptTitle'
  | 'attachmentTitle'
  | 'referenceNo'
>;

const SupplementaryDocuments = ({
  claimId,
  sequenceNo,
  claimStatus,
  receiptFiles = [],
  refLetters = [],
  otherDocs = [],
  receiptTitle,
  attachmentTitle,
  referenceNo,
}: SupplementaryDocumentsProps) => {
  const { t } = useTranslation();
  const { state } = useContext(HomeContext);

  const addDocUrl = getUploadDocPageUrl({ claimId, claimStatus, sequenceNo, referenceNo, path: 'add-documents' });

  const groupDocuments = () => {
    const group: Record<string, string[]> = {};
    // just a workaround, it should be fixed in formatClaimDetails in formatClaimDetails.tsx instead
    // but current logic disallowed frontend to get code for non OP claim type
    if (receiptTitle) {
      group[receiptTitle] = receiptFiles;
    } else if (receiptFiles.length) {
      group[t('claimDetails.receipt')] = receiptFiles;
    }
    if (attachmentTitle) {
      group[attachmentTitle] = refLetters;
    } else if (refLetters.length) {
      group[t('claimDetails.referralLetter')] = refLetters;
    }
    const otherDocsTitle: string = t('claimDetails.otherDocument');
    group[otherDocsTitle] = otherDocs;
    return group;
  };
  const groupDocumentByTitle: Record<string, string[]> = groupDocuments();

  return (
    <div>
      <TitleSection>
        <StyledTitle level={5}>{t('claimDetails.supplementaryDocuments')}</StyledTitle>
        <Text>({t('claimDetails.descriptionText')})</Text>
        {claimStatus === CLAIM_STATUSES.NEED_ACTION && addDocUrl && state.userAccess?.submitClaim && (
          <Link to={addDocUrl}>
            <Button icon={<BravoPlusIcon />}>
              <Text>{t('claimDetails.add')}</Text>
            </Button>
          </Link>
        )}
      </TitleSection>
      <Descriptions bordered column={1}>
        {Object.keys(groupDocumentByTitle).map((key) => {
          if (groupDocumentByTitle[key].length > 0) {
            return (
              <Descriptions.Item label={key} key={key}>
                {groupDocumentByTitle[key].map((filePath) => (
                  <div key={filePath}>
                    <DocumentLink filePath={filePath} key={filePath} />
                  </div>
                ))}
              </Descriptions.Item>
            );
          }
        })}
      </Descriptions>
    </div>
  );
};
export default SupplementaryDocuments;
